/**
 * cineStack.js — геометрия кино-серии: чистая математика для маски ЛЖ. [10.19.24]
 *
 * Здесь три вещи, которые нужны кнопке «ФВ по маске ЛЖ» и которые нельзя
 * оставлять в интерфейсном модуле, потому что их надо проверять числами:
 *
 *   1. МИР → ПИКСЕЛЬ КАДРА. Точка, по которой врач щёлкнул, приходит из
 *      Cornerstone в мировых координатах; зерно для заливки нужно в пикселях
 *      конкретного кадра. Пересчёт — проекция на оси кадра из тегов:
 *      p = IPP + col·ряд·psX + row·столбец·psY, обратно — скалярные произведения.
 *      Ошибиться легко ровно в двух местах, и оба закреплены проверками:
 *      перепутать местами PixelSpacing (он в DICOM идёт «строка, столбец», то
 *      есть [шаг МЕЖДУ строками, шаг между столбцами]) и забыть, что оси кадра
 *      могут быть косыми — на прямой сетке подмена осей невидима.
 *
 *   2. РАСКЛАДКА КИНО: срезы × фазы. Кино короткой оси — одна серия, где кадры
 *      различаются позицией (ImagePositionPatient) и временем в цикле
 *      (TriggerTime). Кадры группируются по позиции, внутри сортируются по
 *      времени; фазы сопоставляются ПО ПОРЯДКОВОМУ НОМЕРУ времени в своём
 *      срезе, а не по равенству миллисекунд — триггерные времена соседних
 *      срезов различаются на единицы миллисекунд, и сравнение «в лоб» разнесло
 *      бы одну фазу по разным группам. Шаг между срезами берётся из самих
 *      позиций (медиана расстояний), а не из тега SliceThickness: толщина среза
 *      и расстояние между срезами — разные вещи, между срезами бывает зазор.
 *
 *   3. ЦЕНТРОИД МАСКИ — для распространения зерна. Полость смещается от базы к
 *      верхушке, и одно зерно на все срезы годится не всегда: зерно следующего
 *      среза — центр масс маски предыдущего. Если центроид попал за пределы
 *      маски (полость в форме подковы — на ЛЖ не бывает, но код не знает, что
 *      ему дали), это возвращается признаком, и вызывающий не обязан ему верить.
 */

const скалярно3 = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/**
 * Мировая точка → пиксель кадра по тегам ориентации.
 *
 * @param {{позиция: number[], ряд: number[], столбец: number[], шагПикселя: [number, number]}} г
 *   позиция — ImagePositionPatient (x00200032);
 *   ряд/столбец — первые/вторые три числа ImageOrientationPatient (x00200037):
 *   направление ВДОЛЬ строки и вдоль столбца;
 *   шагПикселя — PixelSpacing (x00280030) КАК В ТЕГЕ: [между строками, между столбцами].
 * @param {number[]} мир
 * @returns {{x: number, y: number}|null} пиксель (дробный) или null, если геометрии нет
 */
export function мирВПиксель(г, мир) {
  if (!г?.позиция || !г?.ряд || !г?.столбец || !г?.шагПикселя) return null;
  const [шагСтрок, шагСтолбцов] = г.шагПикселя;
  if (!(шагСтрок > 0) || !(шагСтолбцов > 0)) return null;
  const d = [мир[0] - г.позиция[0], мир[1] - г.позиция[1], мир[2] - г.позиция[2]];
  // x — номер СТОЛБЦА: движение вдоль строки, шаг между столбцами.
  // y — номер СТРОКИ: движение вдоль столбца, шаг между строками.
  return {
    x: скалярно3(d, г.ряд) / шагСтолбцов,
    y: скалярно3(d, г.столбец) / шагСтрок,
  };
}

/** Геометрия кадра из сырых DICOM-тегов (строки с «\» как разделителем). */
export function геометрияИзТегов(теги) {
  const числа = (с) => String(с ?? '').split('\\').map(Number).filter(Number.isFinite);
  const позиция = числа(теги?.['x00200032']?.value);
  const ориент = числа(теги?.['x00200037']?.value);
  const шаг = числа(теги?.['x00280030']?.value);
  if (позиция.length !== 3 || ориент.length !== 6 || шаг.length !== 2) return null;
  return {
    позиция,
    ряд: ориент.slice(0, 3),
    столбец: ориент.slice(3, 6),
    шагПикселя: [шаг[0], шаг[1]],
  };
}

/**
 * Раскладка кино: кадры → срезы × фазы.
 *
 * @param {Array<{id: string, позиция: number[], время: number|null, номер?: number}>} кадры
 *   время — TriggerTime (мс) или null; номер — InstanceNumber на случай, если
 *   времени нет вовсе (тогда порядок в срезе берётся по номеру, о чём сказано).
 * @returns {{ок: boolean, причина?: string, срезов?: number, фаз?: number,
 *            шагСрезаММ?: number, сетка?: string[][], поВремени?: boolean,
 *            позиции?: number[][]}}
 *   сетка[срез][фаза] — id кадра.
 */
export function раскладкаКино(кадры) {
  if (!Array.isArray(кадры) || кадры.length < 2) {
    return { ок: false, причина: 'кадров меньше двух — это не кино' };
  }
  const годные = кадры.filter((к) => Array.isArray(к.позиция) && к.позиция.length === 3);
  if (годные.length < 2) return { ок: false, причина: 'у кадров нет позиции (ImagePositionPatient)' };

  // Группировка по позиции: ключ — округлённая до 0,1 мм тройка. Позиции
  // кадров ОДНОГО среза совпадают побайтово или до микрон; 0,1 мм — запас.
  const группы = new Map();
  for (const к of годные) {
    const ключ = к.позиция.map((v) => v.toFixed(1)).join('/');
    if (!группы.has(ключ)) группы.set(ключ, []);
    группы.get(ключ).push(к);
  }

  const поВремени = годные.every((к) => Number.isFinite(к.время));
  const срезы = [...группы.values()].map((г) => {
    const с = [...г];
    с.sort((a, b) => (поВремени ? a.время - b.время : (a.номер || 0) - (b.номер || 0)));
    return с;
  });

  const фаз = срезы[0].length;
  if (фаз < 2 && срезы.length < 2) {
    return { ок: false, причина: 'в серии один срез с одной фазой — ни объёма, ни ФВ из этого не выйдет' };
  }
  if (!срезы.every((с) => с.length === фаз)) {
    const числа = срезы.map((с) => с.length).join(', ');
    return { ок: false, причина: `у срезов разное число фаз (${числа}) — серия неполна или это не кино` };
  }

  // Порядок срезов и шаг — вдоль нормали к кадру: проекция позиции на векторное
  // произведение осей. Сортировка по одной координате ломается на косых сериях.
  const п = срезы[0][0];
  let нормаль = [0, 0, 1];
  const первый = кадры.find((к) => к.ряд && к.столбец);
  if (первый) {
    const r = первый.ряд, c = первый.столбец;
    нормаль = [r[1] * c[2] - r[2] * c[1], r[2] * c[0] - r[0] * c[2], r[0] * c[1] - r[1] * c[0]];
  }
  void п;
  срезы.sort((a, b) => скалярно3(a[0].позиция, нормаль) - скалярно3(b[0].позиция, нормаль));

  let шагСрезаММ = NaN;
  if (срезы.length > 1) {
    const шаги = [];
    for (let i = 1; i < срезы.length; i++) {
      const a = срезы[i - 1][0].позиция, b = срезы[i][0].позиция;
      шаги.push(Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]));
    }
    шаги.sort((x, y) => x - y);
    шагСрезаММ = шаги[Math.floor(шаги.length / 2)];
  }

  return {
    ок: true,
    срезов: срезы.length,
    фаз,
    шагСрезаММ,
    поВремени,
    сетка: срезы.map((с) => с.map((к) => к.id)),
    позиции: срезы.map((с) => с[0].позиция),
  };
}

/**
 * Центр масс маски — зерно для соседнего среза.
 *
 * @returns {{x: number, y: number, внутри: boolean}|null} null на пустой маске;
 *   внутри=false, когда центроид попал вне маски (форма вогнутая) — тогда
 *   берётся ближайший к центроиду пиксель маски, и это тоже возвращается.
 */
export function центроидМаски(маска, ширина, высота) {
  let n = 0, сх = 0, су = 0;
  for (let y = 0; y < высота; y++) {
    for (let x = 0; x < ширина; x++) {
      if (!маска[y * ширина + x]) continue;
      n++; сх += x; су += y;
    }
  }
  if (!n) return null;
  let x = Math.round(сх / n), y = Math.round(су / n);
  if (маска[y * ширина + x]) return { x, y, внутри: true };
  // Центроид вне маски: ближайший пиксель маски — честная замена.
  let лучший = null, лучшее = Infinity;
  for (let py = 0; py < высота; py++) {
    for (let px = 0; px < ширина; px++) {
      if (!маска[py * ширина + px]) continue;
      const d = (px - x) * (px - x) + (py - y) * (py - y);
      if (d < лучшее) { лучшее = d; лучший = { x: px, y: py, внутри: false }; }
    }
  }
  return лучший;
}

export default { мирВПиксель, геометрияИзТегов, раскладкаКино, центроидМаски };
